import MeridianLockup from "./MeridianLockup";

/**
 * MER-171: first-run onboarding overlay for fresh tenants.
 *
 * Renders a centered welcome card over the dashboard when the
 * tenant's `service_event` row count is zero (see `useHasCanonicalData`).
 * Two CTAs:
 *   - "Upload data" (primary, brand gradient) opens the per-tenant
 *     external upload portal in a new tab.
 *   - "Skip for now" calls `onDismiss` to suppress the overlay for
 *     the rest of the session.
 *
 * Once the customer uploads + the next sync lands service_event rows,
 * `useHasCanonicalData` flips to true and the overlay no longer renders
 * regardless of the dismissed state.
 */
export interface FirstRunOverlayProps {
  slug: string;
  onDismiss: () => void;
}

export default function FirstRunOverlay({ slug, onDismiss }: FirstRunOverlayProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-run-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.10), transparent 50%), rgba(15, 31, 42, 0.42)",
        display: "grid",
        placeItems: "center",
        padding: "32px",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "40px 40px 32px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 12px 32px rgba(15, 31, 42, 0.12)",
          display: "grid",
          gap: 20,
          textAlign: "center",
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 16 }}>
          <MeridianLockup size="lg" align="stack" />
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <h1 id="first-run-title" style={{ fontSize: 22, fontWeight: 600, color: "var(--text)" }}>
            Welcome to Meridian
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>
            Your dashboard is ready. Upload your first data file to see your operations come to
            life.
          </p>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <a
            href={`https://upload.meridiananalytics.io/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={primaryButtonStyle}
          >
            Upload data
          </a>
          <button type="button" onClick={onDismiss} style={skipButtonStyle}>
            Skip for now
          </button>
        </div>

        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 6,
            paddingTop: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Need help?{" "}
            <a
              href="mailto:support@meridiananalytics.io"
              style={{ color: "var(--brand-deep)", textDecoration: "none" }}
            >
              support@meridiananalytics.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
  textDecoration: "none",
  textAlign: "center",
  display: "block",
};

const skipButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 500,
  padding: "8px 14px",
  borderRadius: 10,
  border: "none",
  background: "transparent",
  color: "var(--text-muted)",
  cursor: "pointer",
};
